import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import {
  lowDiskSpaceAcceptedAfterTsAtom,
  lowDiskSpaceAtom,
} from '@/atoms/lowDiskSpaceAtom';
import { offlineActiveRecordingIdAtom } from '@/atoms/offline-recording/sync';
import { tauriAudioLevelsAtom } from '@/atoms/tauriAudioLevelsAtom';
import { wavWriterFailureAtom } from '@/atoms/wavWriterFailureAtom';
import { RecordingError } from '@/models/tauriRecordErrors';
import { store } from '@/store';
import { translateDesktopError } from '@/tauri/errors';
import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { toast, type ToastTone } from '@heidi/design-system/toast';
import {
  AUDIO_CAPTURE_ACTIVITY_EVENT,
  bridgeErrorKind,
  DISK_SPACE_LOW_EVENT,
  focusTauriWindow,
  getCurrentWindowLabel,
  IpcInvokeError,
  ipcInvokeRaw,
  isIpcLessWindow,
  MAIN_WINDOW_LABEL,
  shouldAcceptDiskSpaceLow,
  shouldAcceptWavWriterFailure,
  WAV_WRITER_HEALTH_EVENT,
  type AudioCaptureActivityPayload,
  type DiskSpaceLowPayload,
  type IpcEventPayload,
  type IpcInvokeOptions,
  type IpcResult,
  type WavWriterFailurePayload,
} from '@heidi/plugin-core';
import { getCurrentWindow } from '@tauri-apps/api/window';
import i18next from 'i18next';
import { getDefaultStore } from 'jotai';
import Router from 'next/router';
// Through the SHIM, not the door, and deliberately. `@/tauri/errorDetection` is
// a pure re-export of `@heidi/plugin-core`, so the implementation
// under these four names is the package's either way — but the ERROR-event suite
// below has three `jest.mock`/`jest.doMock('@/tauri/errorDetection', …)` blocks
// controlling the string-matching fallback path, and repointing this import at
// the door would leave all three intercepting nothing. The repo's shim rule
// exists for exactly this: "existing importers and their jest.mock literals must
// keep resolving unchanged."
import { trackTauriErrorEvent } from '@/lib/analytics/scribe/errors';
import {
  isDeviceLostError,
  isMicrophoneEmptyError,
  isPermissionDeniedError,
  isSpeakerEmptyError,
} from './errorDetection';
import { offlineDeviceLostMessages, sckCaptureFailureMessages } from './ipc.messages';
import { isTauri } from './utils';

// `IpcInvokeError` is re-exported because this module has always exported it and
// its consumers name `@/tauri/ipc`. The class itself now lives in
// `@heidi/plugin-core` so `ipcInvokeRaw` can throw it.
export { IpcInvokeError };

function t(key: string, fallback: string): string {
  const translated = i18next.t(key, { defaultValue: '' });
  return translated && translated !== key ? translated : fallback;
}

/**
 * The origin value marking an event as belonging to the Rust-owned offline
 * recorder. Mirrors `CAPTURE_ORIGIN_OFFLINE_RECORDING` in
 * `src-tauri/src/utils/event.rs`; the two must be changed together. Both
 * origin-stamped events use it — `ERROR` under the key `capture_origin`, and
 * `AUDIO_CAPTURE_ACTIVITY` under `captureOrigin` (each payload keeps its own
 * serde convention; the value is the same).
 */
const CAPTURE_ORIGIN_OFFLINE_RECORDING = 'offline_recording';

/**
 * The origin value marking an event as the online recording model's. Mirrors
 * `CAPTURE_ORIGIN_ONLINE` in `src-tauri/src/utils/event.rs`.
 *
 * It exists so that "online" and "this desktop binary is too old to say" stop
 * being the same bytes. While they were, both fell back to
 * `offlineActiveRecordingIdAtom`, and the atom is wrong about a live online
 * capture whenever it is set — which it can be, both by a genuine overlap and by
 * a stale value, since nothing clears it on teardown or on a Rust-side failure
 * that emits neither `capture_ended` nor `pending_sync`.
 */
const CAPTURE_ORIGIN_ONLINE = 'online';

/**
 * What {@link classifyCaptureOrigin} decided, and the one input a caller still
 * needs after it has decided.
 *
 * Exactly what the two listeners consume, and no more. The atom's own reading
 * — whether it alone says an offline capture is in flight — is an argument to
 * {@link classifyFromTag} and stops there; returning it would publish a second,
 * weaker verdict beside the real one, which is the thing a caller must not
 * branch on.
 */
type CaptureOriginClassification = {
  /**
   * `offlineActiveRecordingIdAtom`, read once so the log line and the verdict
   * agree. Returned for the device-lost log, not to be re-classified from.
   */
  offlineRecordingId: string | null;
  /** Whether this event must be kept away from the online, server-mutating consumers. */
  isOfflineCapture: boolean;
};

/**
 * Which recorder produced an origin-stamped event.
 *
 * One mechanism for both stamped events. Four rules:
 *
 * 1. **Absent means UNKNOWN, never "online"** — fall back to the atom, exactly
 *    the classification that shipped before the field existed. Only a desktop
 *    binary older than the field omits the key now; every live capture names
 *    itself.
 * 2. **An offline tag is authoritative.**
 * 3. **An online tag is authoritative too.** This is the rule that was missing,
 *    and it could not be written before, because Rust stamped nothing for an
 *    online capture and there was no tag to trust. Without it a live online
 *    capture's event was classified offline whenever the atom was set — on the
 *    `AUDIO_CAPTURE_ACTIVITY` path that silently discarded a real silence
 *    transition and the clinician was never warned. The producer that owns the
 *    stream is the one that says whose it is; that beats an app-wide atom the
 *    frontend maintains at second hand.
 * 4. **Any OTHER tag still defers to a live offline capture** — a value neither
 *    side knows, i.e. a future recorder. Deliberately generous for the same
 *    reason as before: a missed notification costs a toast or a warning, and a
 *    wrong call costs server mutations against a session the backend never saw.
 *
 * @param captureOrigin the event's own tag, whichever key it arrived under.
 * @param frontendBelievesOffline what `offlineActiveRecordingIdAtom` alone says.
 */
function classifyFromTag(
  captureOrigin: string | undefined,
  frontendBelievesOffline: boolean
): boolean {
  // Rule 1: no tag, no evidence — the atom is all there is.
  if (captureOrigin === undefined) return frontendBelievesOffline;
  // Rule 2 and rule 3: the producer named itself, so take it at its word.
  if (captureOrigin === CAPTURE_ORIGIN_OFFLINE_RECORDING) return true;
  if (captureOrigin === CAPTURE_ORIGIN_ONLINE) return false;
  // Rule 4: a tag from a recorder this build has never heard of.
  return frontendBelievesOffline;
}

/**
 * {@link classifyFromTag} against the one atom read, packaged so the log line
 * and the verdict in each listener are guaranteed to agree.
 *
 * @param jotaiStore the store `listenToEvent` owns — passed in rather than
 *   re-resolved here so every reader in one event sees one snapshot.
 * @param captureOrigin the event's own tag, whichever key it arrived under.
 */
function classifyCaptureOrigin(
  jotaiStore: ReturnType<typeof getDefaultStore>,
  captureOrigin: string | undefined
): CaptureOriginClassification {
  const offlineRecordingId = jotaiStore.get(offlineActiveRecordingIdAtom);
  return {
    offlineRecordingId,
    isOfflineCapture: classifyFromTag(captureOrigin, offlineRecordingId !== null),
  };
}

const HISTORY: Map<string, number> = new Map();

/**
 * Codes describing a failure that is STILL HAPPENING when the toast is shown,
 * and whose remedy is something the clinician has to do.
 *
 * `showEventNotification`'s default is five seconds, which suits a failure that
 * has already resolved itself: the clinician either sees it or has missed
 * nothing. It does not suit this class. Rust reports a data-integrity failure
 * once per capture and deliberately never re-arms — repeating it from a
 * real-time audio callback is the flood the latch exists to remove — so the
 * five-second window is not a first chance among many, it is the ONLY
 * announcement the clinician will get while audio keeps being dropped for the
 * rest of the session. A main window that is minimised or behind another app
 * for those five seconds means they are never told at all.
 *
 * A longer toast is the floor, not the answer. A failure that persists wants a
 * surface that persists — the `WAV_WRITER_HEALTH_FAILED` listener below sets
 * state and lets a durable surface render it, which is the shape this class
 * really wants. Choosing that surface is a product decision and is not made
 * here; until it is, this at least survives a glance away.
 */
const ONGOING_FAILURE_ERROR_CODES: ReadonlySet<string> = new Set(['RECORDING_FAILED']);

/**
 * Long enough to survive looking away from the screen, and comfortably longer
 * than the ten seconds the offline device-loss toast uses — that failure is
 * announced once too, but the capture it describes has already stopped, so it
 * is the less severe of the two.
 */
const ONGOING_FAILURE_TOAST_DURATION_MS = 30000;

/**
 * Small wrapper on top of tauri api invoke for a light abstraction.
 *
 * THE APP-SIDE ADAPTER over `@heidi/plugin-core`'s `ipcInvokeRaw`.
 * The envelope — dynamic-import the SDK, `invoke`, unwrap `{ result, error }`,
 * throw `IpcInvokeError` — is pure and lives in the package. What stays here is
 * everything the package may not hold:
 *
 *   the `isTauri()` guard        `@heidi/runtime/platform` is `type:runtime`, a
 *                               band `type:bridge` may not depend on (§1b) — and
 *                               the guard exists to produce this module's
 *                               "Not running in Tauri environment" message
 *   the `logger` calls          same band, same refusal
 *   `translateDesktopError`     i18next is out of the package by owner ruling
 *   `showEventNotification`     a toast: product error policy, app-side by the
 *                               architecture's own sentence
 *   `suppressErrorCodes`        toast policy, so the package publishes the type
 *                               and never reads it
 *
 * Signature and observable behaviour are unchanged, which is what makes the
 * split invisible to the four `src/tauri/**` modules that call this.
 */
export async function ipc_invoke(
  method: string,
  params?: object,
  options?: IpcInvokeOptions
): Promise<IpcResult> {
  if (isTauri()) {
    // Keys only: param values can carry device names and file paths, and
    // non-prod builds send debug lines to Datadog.
    logger.debug('ipc_invoke', { method, paramKeys: params ? Object.keys(params) : [] });
    try {
      return await ipcInvokeRaw(method, params);
    } catch (error) {
      if (error instanceof IpcInvokeError) {
        const errorMessage = error.message;
        const errorCode = error.errorCode;
        logger.debug('ipc_invoke error', { errorKind: bridgeErrorKind(error) });
        if (!errorCode || !options?.suppressErrorCodes?.includes(errorCode)) {
          const displayMessage = translateDesktopError(errorMessage, errorCode);
          showEventNotification(
            t('desktop.errors.titles.error', 'Error'),
            displayMessage,
            'destructive',
            4000
          );
        }
      }
      // Classification only: params can carry user content (device names,
      // file paths) and the Rust error message is a free-form string, and both
      // would reach Datadog unredacted. Param keys are code-defined field names.
      logger.error('ERROR - ipc_invoke - error', {
        method,
        paramKeys: params ? Object.keys(params) : [],
        errorKind: bridgeErrorKind(error),
      });
      throw error;
    }
  } else {
    throw new Error('Not running in Tauri environment');
  }
}

// Deep links can carry credentials or identifiers in the query string or
// fragment, so a log line gets the scheme, host and path only.
function deepLinkForLog(url: string | undefined): string {
  return typeof url === 'string' ? (url.split(/[?#]/, 1)[0] ?? '') : '';
}

// Helper function to handle deep link URLs
async function handleDeepLinkUrl(url: string): Promise<void> {
  try {
    logger.info('Handling deep link:', deepLinkForLog(url));

    // Check authentication first - ignore deep link if not authenticated
    const token = store.getState()?.auth?.token;
    if (!token) {
      await focusTauriWindow();
      logger.warn('Deep link ignored - user not authenticated:', deepLinkForLog(url));
      return;
    }

    if (url?.includes('stripe-redirect')) {
      logger.info('Navigating to /scribe/stripe/success');
      await Router.push('/scribe/stripe/success');
    } else if (url?.includes('session')) {
      const sessionUrl = url.split('session/')[1]?.split('?')[0] ?? '';
      if (sessionUrl) {
        logger.info(`Navigating to /scribe/session/${deepLinkForLog(sessionUrl)}`);
        await Router.push(`/scribe/session/${sessionUrl}`);
      }
    }

    // Focus the window after navigation
    await focusTauriWindow();
  } catch (error) {
    // A Router.push error can echo the href, so log only status, code and a
    // bounded kind, never the message.
    logger.error('ERROR - deep link handling - error', {
      ...sanitizeError(error),
      errorKind: bridgeErrorKind(error),
    });
  }
}

async function listenToEvent(): Promise<void> {
  if (isTauri()) {
    logger.info('[Deep Link] isTauri() check passed, initializing listeners...');
    const { listen } = await import('@tauri-apps/api/event');
    const jotaiStore = getDefaultStore();
    await listen<IpcEventPayload>('ERROR', (event) => {
      logger.debug('ERROR - Event', event);
      try {
        // READ THIS BEFORE COUNTING THESE EVENTS AS CAPTURES. Deliberately
        // window-independent, for the reason on the generic toast below — but
        // that has a consequence for anyone querying the result. This listener
        // is registered in EVERY Tauri webview and the producers broadcast, so
        // one failure lands here once per open window. Rust now reports a
        // data-integrity failure once per capture, yet the count here is
        // captures × open webviews, and that multiplier moves with how many
        // auxiliary windows the clinician happens to have open. An online
        // versus offline comparison drawn from the raw count is comparing that
        // multiplier as much as the failure rate.
        trackTauriErrorEvent(event.payload);
        if (event.payload) {
          const { message, error_code } = event.payload;
          const displayMessage = translateDesktopError(message, error_code);

          // Audio Device Lost / Disconnected scenarios
          // Prefer error_code, fall back to string matching for older Rust versions
          if (error_code === 'AUDIO_DEVICE_LOST' || isDeviceLostError(event.payload)) {
            const isRunning = store.getState().timerModel.isRunning;
            const currentDevice =
              store.getState().scribeRecordModel.tauriInputDeviceSelected?.name;
            // Which recorder produced this? See `IpcEventPayload.capture_origin`
            // for why the event's own tag beats the atom, and
            // `classifyCaptureOrigin` for the three rules.
            const captureOrigin = event.payload.capture_origin;
            const { offlineRecordingId, isOfflineCapture: isOfflineCaptureError } =
              classifyCaptureOrigin(jotaiStore, captureOrigin);
            // No message or device name: both can carry the owner's name.
            logger.warn('[DeviceLost] Device disconnection detected', {
              messageLength: message?.length ?? 0,
              // Which branch admitted it: the code, or the string fallback.
              errorCode: error_code ?? 'absent',
              isRunning,
              hasCurrentDevice: currentDevice !== undefined,
              offlineRecordingId,
              captureOrigin: captureOrigin ?? 'absent',
              isOfflineCaptureError,
            });

            // An offline capture is Rust-owned and has no server session, while
            // `AudioDeviceLostRecovery` resolves its session from the route — so
            // handing off to it would fire change-audio-input / stop-recording
            // against an id the backend has never seen. Notify and arm no
            // recovery; what happens to the capture afterwards is a product
            // decision this fix does not make.
            //
            // Copy says only what happens: the producer's control loop ignores
            // `err_fn`, so the capture stays live and the WAV writer pads
            // silence to hold the timeline. Nothing auto-switches, nothing
            // stops, and reconnecting does not revive the dead cpal stream — so
            // ending the session is the only true remedy to offer.
            if (isOfflineCaptureError) {
              // The early return matters in both branches below: it keeps every
              // webview from falling through to the MICROPHONE_WITH_ISSUE
              // dispatch, which would arm the server-mutating online recovery
              // against a local-only offline session.
              if (captureOrigin === CAPTURE_ORIGIN_OFFLINE_RECORDING) {
                // Rust owns this capture and is already reopening it on another
                // microphone; only it knows whether that worked. Staying silent
                // is safe *because the tag is explicit* — the outcome arrives on
                // `offline_recording:input_recovered` / `:input_recovery_failed`
                // (handled in `useOfflineRecordingEvents`). Toasting here would
                // guess the outcome a beat before it is decided, and the guess
                // is wrong in the common case: capture usually continues.
                //
                // This mirrors the online path, which has always waited for the
                // outcome — `audio-device-lost-recovery.tsx` toasts "continues
                // on {device}" or "stopped", never on the raw device-loss error.
                return;
              }

              // Classified offline WITHOUT an offline tag, which now means one
              // of two things: no tag at all (a desktop binary older than the
              // field, where the atom is the only signal there is), or a tag
              // from a recorder this build has never heard of. An explicitly
              // ONLINE capture no longer reaches here — it is trusted and sent
              // to the recovery below, which is what rule 3 in
              // `classifyCaptureOrigin` is for.
              //
              // For the two cases that DO reach here the fallback may still be
              // wrong, and the offline recorder is not the producer of such an
              // error, so it will never emit a recovery event for it. Deferring
              // to Rust would drop the notification entirely and the clinician
              // would be told nothing at all.
              //
              // Say the one thing that is true regardless of which recorder it
              // was — the microphone is gone and ending the session preserves
              // what was captured — without arming the online recovery.
              //
              toastInMainWindowOnly(
                formatMessageOutsideReact(offlineDeviceLostMessages.title),
                formatMessageOutsideReact(offlineDeviceLostMessages.description),
                'destructive',
                10000
              );
              return;
            }

            // APP-7375 (Level A): hand off to AudioDeviceLostRecovery, which
            // auto-switches to the next available microphone (or stops + finalizes
            // if none remain) and shows a single non-blocking toast. We intentionally
            // do NOT fire a destructive "device lost" notification here — that would
            // double-notify before the auto-switch toast.
            store.dispatch.tauriRecordModel.setRecordingError(
              RecordingError.MICROPHONE_WITH_ISSUE
            );
            return;
          }

          // ScreenCaptureKit -3801 (Screen Recording TCC denial) is new copy
          // for this specific failure (DSK-740), so it goes on react-intl.
          // AUDIO_SCK_ERROR is the pre-existing generic capture-failure code
          // with shipped translations in `desktop.errors.audio.sckError`
          // (see `src/tauri/errors/audio.ts`) — leave it on that path rather
          // than migrating it here, or every non-English user regresses to
          // English on a string that was already translated.
          if (error_code === 'AUDIO_SCK_PERMISSION_DENIED') {
            toastInMainWindowOnly(
              formatMessageOutsideReact(sckCaptureFailureMessages.permissionDeniedTitle),
              formatMessageOutsideReact(sckCaptureFailureMessages.permissionDenied),
              'destructive'
            );
            return;
          }

          // Permission denied errors
          if (
            error_code === 'AUDIO_PERMISSION_DENIED' ||
            isPermissionDeniedError(event.payload)
          ) {
            toastInMainWindowOnly(
              t('desktop.errors.titles.permissionDenied', 'Permission Denied'),
              displayMessage,
              'destructive'
            );
            return;
          }

          // Microphone / Speaker empty (set UI state, but don't early-return — still show toast below)
          if (error_code === 'AUDIO_MIC_EMPTY' || isMicrophoneEmptyError(event.payload)) {
            store.dispatch.tauriRecordModel.setCurrentRecordingError(
              RecordingError.NO_AUDIO_IN_MIC
            );
          }

          if (
            error_code === 'AUDIO_SPEAKER_EMPTY' ||
            isSpeakerEmptyError(event.payload)
          ) {
            store.dispatch.tauriRecordModel.setCurrentRecordingError(
              RecordingError.NO_AUDIO_IN_SPEAKER
            );
          }

          // Show error notification (covers all error codes including mic/speaker empty)
          toastInMainWindowOnly(
            t('desktop.errors.titles.error', 'Error'),
            displayMessage,
            'destructive',
            error_code && ONGOING_FAILURE_ERROR_CODES.has(error_code)
              ? ONGOING_FAILURE_TOAST_DURATION_MS
              : undefined
          );
        }
      } catch (error) {
        logger.debug('Error in listening for ERROR event', error);
      }
    });

    // AUDIO_LEVELS is registered by ./events/audio-levels instead — this
    // function runs in every webview, so a module-load listener burned CPU in
    // windows that render no bars.

    // Silent-capture triggers from the Rust silence monitor — main window
    // only: `listenToEvent()` runs in EVERY Tauri window and each webview
    // holds its own Redux store, so registering here guarantees a single
    // consumer against a single store. The listener only maintains state —
    // flag checks and all policy live in `CaptureSilenceRecovery`, so a
    // mid-recording flag flip needs no listener re-registration.
    if (getCurrentWindow().label === MAIN_WINDOW_LABEL) {
      // Gated at REGISTRATION rather than with an inline `getCurrentWindow()`
      // check inside the handler, because this handler is toast-only: there is
      // nothing window-independent left to run once the toast is gone, so the
      // cheapest and least forgettable place to narrow it is here. The `ERROR`
      // listener above cannot do the same — its classification, telemetry and
      // store writes must keep running in every webview, so only its toast can
      // be narrowed.
      //
      // Same multiplier either way: the producers broadcast, this function runs
      // in every Tauri webview, and each one mounts its own `<Toaster />`, so an
      // ungated registration renders one warning toast per open window for a
      // single event.
      await listen<IpcEventPayload>('WARNING', (event) => {
        logger.debug('WARNING - Event', event);
        const displayMessage = translateDesktopError(
          event.payload.message,
          event.payload.error_code
        );
        showEventNotification(
          t('desktop.errors.titles.warning', 'Warning'),
          displayMessage,
          'warning'
        );
      });

      await listen<WavWriterFailurePayload>(WAV_WRITER_HEALTH_EVENT, (event) => {
        const payload = event?.payload;
        if (!payload) return;
        const recordState = store.getState().tauriRecordModel;
        const currentFailure = jotaiStore.get(wavWriterFailureAtom);
        if (
          !shouldAcceptWavWriterFailure(
            payload,
            recordState.chunks.session_id,
            currentFailure,
            recordState.captureActivityHighWater[payload.source],
            recordState.captureActivityAcceptedAfterTs
          )
        ) {
          logger.debug('[WavWriterHealth] Discarding stale or duplicate failure', {
            source: payload.source,
            phase: payload.phase,
            errorCode: payload.errorCode,
            captureInstanceId: payload.captureInstanceId,
          });
          return;
        }
        // Session ids are intentionally omitted from production logs. The
        // session-scoped identifier is only used locally to reject stale IPC.
        logger.error('[WavWriterHealth] Terminal writer failure', {
          source: payload.source,
          phase: payload.phase,
          errorCode: payload.errorCode,
          rawOsError: payload.rawOsError,
          attemptedSamples: payload.attemptedSamples,
          committedSamples: payload.committedSamples,
        });
        jotaiStore.set(tauriAudioLevelsAtom, undefined);
        jotaiStore.set(wavWriterFailureAtom, payload);
      });

      // Proactive low-disk warning from the Rust disk-space monitor. State
      // only — the banner (`LowDiskAlert`) owns the flag check and rendering,
      // so a mid-capture flag flip needs no listener re-registration.
      await listen<DiskSpaceLowPayload>(DISK_SPACE_LOW_EVENT, (event) => {
        const payload = event?.payload;
        if (!payload) return;
        // The Rust recorder survives a WebView reload while Rematch state does
        // not. Boot recovery restores this Jotai owner atom, so it is the
        // canonical identity for warnings emitted after renderer recovery.
        const activeSessionId = jotaiStore.get(activeSessionIdAtom);
        const currentWarning = jotaiStore.get(lowDiskSpaceAtom);
        const acceptedAfterTs = jotaiStore.get(lowDiskSpaceAcceptedAfterTsAtom);
        if (
          !shouldAcceptDiskSpaceLow(
            payload,
            activeSessionId,
            currentWarning,
            acceptedAfterTs
          )
        ) {
          logger.debug('[DiskSpace] Discarding stale or duplicate warning', {
            errorCode: payload.errorCode,
            availableBytes: payload.availableBytes,
          });
          return;
        }
        // Session ids are intentionally omitted from production logs.
        logger.warn('[DiskSpace] Low disk space for active capture', {
          errorCode: payload.errorCode,
          availableBytes: payload.availableBytes,
          thresholdBytes: payload.thresholdBytes,
        });
        jotaiStore.set(lowDiskSpaceAtom, payload);
      });

      await listen<AudioCaptureActivityPayload>(AUDIO_CAPTURE_ACTIVITY_EVENT, (event) => {
        const payload = event?.payload;
        if (!payload || (payload.source !== 'mic' && payload.source !== 'speaker')) {
          return;
        }
        // Origin gate, and it runs before the three anti-staleness filters
        // below because it is not one of them — those ask "is this event from
        // the recording we are showing?", this asks "may this event reach the
        // online recovery at all?".
        //
        // `CaptureSilenceRecovery` resolves its session from
        // `tauriRecordModel.chunks.session_id` (set only by the ONLINE start
        // mutation) falling back to the route, which during an offline capture
        // yields the LOCAL session id. Its `timerModel.isRunning` gate does not
        // close either — `useRecordingButton` drives `onStart` on the offline
        // branch too. So the origin is the only thing that can gate this, and
        // without it an armed offline monitor would drive change-audio-input /
        // stop-recording against an id the backend has never seen. Exactly the
        // failure `AUDIO_DEVICE_LOST` was fixed for; same mechanism, same rules
        // — see `classifyCaptureOrigin`.
        //
        // The gate has to be precise in BOTH directions, and that is why the
        // online tag exists. Dropping is silent here, so a gate that over-fires
        // costs a silence warning the clinician never sees — on a path that
        // works today for every online desktop user, which is a worse trade
        // than the one this ticket is fixing. An online capture now says so, so
        // it is never dropped on the strength of an atom that is describing a
        // different recorder.
        //
        // No notification in either branch, unlike device loss: there is
        // nothing for the clinician to be told here that the offline path does
        // not own, so both cases are a silent drop.
        const { isOfflineCapture } = classifyCaptureOrigin(
          jotaiStore,
          payload.captureOrigin
        );
        if (isOfflineCapture) {
          // Two drops, logged at two levels, because only one of them can be
          // wrong. An explicit offline tag is the producer naming itself, so
          // that drop is routine and stays at `debug`.
          //
          // Everything else reaching here was classified offline by the ATOM —
          // no tag (rule 1) or a tag this build does not know (rule 4) — and a
          // wrong verdict there costs a silence warning the clinician never
          // sees, silently. Rust and the webview ship as one artifact
          // (`frontendDist: ../out` in every profile), so a shipped build
          // should never produce either case; that makes this an invariant
          // worth asserting rather than a noisy log. `debug` could not carry
          // it — `@heidi/runtime/logger` sets pino to `silent` when `isProd`
          // and drops anything below `info` before Datadog — so it would leave
          // no trace in exactly the builds where it matters. `warn` matches
          // what the device-lost branch already does with its uncertain case,
          // and gives slice 2 something to check after ship instead of an
          // assumption that the gate behaves.
          if (payload.captureOrigin === CAPTURE_ORIGIN_OFFLINE_RECORDING) {
            logger.debug('[CaptureSilence] Discarding an offline capture event', {
              source: payload.source,
              state: payload.state,
              captureOrigin: payload.captureOrigin,
            });
          } else {
            logger.warn(
              '[CaptureSilence] Discarding an event classified offline by the atom alone',
              {
                source: payload.source,
                state: payload.state,
                captureOrigin: payload.captureOrigin ?? 'absent',
              }
            );
          }
          return;
        }
        // Session-boundary filter: a genuine start resets the high-water
        // marks to 0, so instance-id filtering alone cannot stop a delayed
        // event from recording A arriving after recording B started. When the
        // monitor stamps a session id, require it to match the recording the
        // FE currently owns (`chunks.session_id`, set before the recorder
        // starts). Events without one fall through to the instance-id filter
        // — the field is optional in the contract.
        const activeSessionId = store.getState().tauriRecordModel.chunks.session_id;
        if (payload.sessionId && payload.sessionId !== activeSessionId) {
          logger.debug('[CaptureSilence] Discarding event for a different session', {
            source: payload.source,
            state: payload.state,
            eventSessionId: payload.sessionId,
            activeSessionId,
          });
          return;
        }
        // Drop events from a torn-down monitor so they can't poison a
        // restarted recording's state. High-water marks reset per recording
        // start in useMutateStartTauriRecording.
        const highWater =
          store.getState().tauriRecordModel.captureActivityHighWater[payload.source];
        if (payload.captureInstanceId < highWater) {
          logger.debug('[CaptureSilence] Discarding stale monitor event', {
            source: payload.source,
            state: payload.state,
            captureInstanceId: payload.captureInstanceId,
            highWater,
          });
          return;
        }
        // Timestamp fence: the id high-water can't fence a source that never
        // emitted this recording (its mark is stale, so the restart's +1 bump
        // undershoots the torn-down monitor's real id). Every reset dispatches
        // after the old monitors are down, so any event EMITTED before the
        // reset is from a dead monitor regardless of its id.
        const acceptedAfterTs =
          store.getState().tauriRecordModel.captureActivityAcceptedAfterTs;
        if (payload.ts < acceptedAfterTs) {
          logger.debug('[CaptureSilence] Discarding pre-reset monitor event', {
            source: payload.source,
            state: payload.state,
            eventTs: payload.ts,
            acceptedAfterTs,
          });
          return;
        }
        if (payload.state === 'silent') {
          logger.warn('[CaptureSilence] Source reported silent', {
            source: payload.source,
            silenceSecs: payload.silenceSecs,
            seenEnergetic: payload.seenEnergetic,
            captureInstanceId: payload.captureInstanceId,
            sessionId: payload.sessionId,
          });
          store.dispatch.tauriRecordModel.setCaptureSilence({
            source: payload.source,
            entry: {
              silenceSecs: payload.silenceSecs,
              seenEnergetic: payload.seenEnergetic,
              captureInstanceId: payload.captureInstanceId,
            },
          });
        } else if (payload.state === 'active') {
          logger.info('[CaptureSilence] Source recovered', {
            source: payload.source,
            captureInstanceId: payload.captureInstanceId,
          });
          store.dispatch.tauriRecordModel.clearCaptureSilence({
            source: payload.source,
            captureInstanceId: payload.captureInstanceId,
          });
        }
      });
    }

    // Deep link handling — main window only.
    //
    // `listenToEvent()` runs in EVERY Tauri window (it's a module-load side
    // effect of importing this file from `_app.tsx`, executed on each window's
    // webview). Deep links drive main-app navigation (`/scribe/session/...`),
    // so only the main window should process them. The auxiliary windows
    // (overlay, recording-toolbar, dictation-widget) don't grant `deep-link:*`
    // permissions, so calling `getCurrent()` / `onOpenUrl()` there throws an
    // uncaught "Command plugin:deep-link|get_current not allowed by ACL"
    // (APP-6060). `getCurrentWindow()` reads the injected window label with no
    // IPC, so it's safe to call in any window.
    if (getCurrentWindow().label === MAIN_WINDOW_LABEL) {
      const { getCurrent, onOpenUrl } = await import('@tauri-apps/plugin-deep-link');

      const startupUrls = await getCurrent();
      if (startupUrls && startupUrls.length > 0 && startupUrls[0]) {
        logger.info(
          '[Deep Link] App started with deep link:',
          deepLinkForLog(startupUrls[0])
        );
        void handleDeepLinkUrl(startupUrls[0]);
      }

      await onOpenUrl((urls) => {
        logger.info('[Deep Link] *** onOpenUrl callback triggered! ***', {
          count: Array.isArray(urls) ? urls.length : 1,
        });
        const url = Array.isArray(urls) ? urls[0] : urls;
        if (url) {
          logger.info(
            '[Deep Link] Processing URL from running app:',
            deepLinkForLog(url)
          );
          void handleDeepLinkUrl(url);
        }
      });
      logger.info('[Deep Link] onOpenUrl listener registered successfully');
    }
  } else {
    logger.info('[Deep Link] Not in Tauri environment, skipping');
  }
}

/**
 * The one gate for every toast the `ERROR` listener raises.
 *
 * That listener is registered in EVERY Tauri webview and the producers
 * broadcast, so an ungated toast renders one copy per open window for a single
 * failure. Gating per branch is what let the bespoke permission toasts keep
 * multiplying while the fall-through one was narrowed, so the check lives at the
 * funnel instead: a new branch is covered by construction. Only the toast is
 * narrowed — classification, telemetry and store writes stay window-independent.
 */
function toastInMainWindowOnly(
  title: string,
  description: string,
  tone: ToastTone = 'default',
  duration: number = 5000
) {
  if (getCurrentWindow().label !== MAIN_WINDOW_LABEL) return;
  showEventNotification(title, description, tone, duration);
}

function showEventNotification(
  title: string,
  description: string,
  tone: ToastTone = 'default',
  duration: number = 5000
) {
  try {
    const currentTime = new Date().getTime();
    if (!HISTORY.has(description) || currentTime - HISTORY.get(description)! > duration) {
      toast({
        title,
        description,
        duration,
        tone,
      });
      HISTORY.set(description, currentTime);
    }
  } catch (error) {
    logger.error('ERROR - showEventNotification - error', error);
  }
}

// Initialize event listeners only on client-side. A window whose capability
// grants no IPC (the CUA border) rejects even `listen`.
if (
  typeof window !== 'undefined' &&
  isTauri() &&
  !isIpcLessWindow(getCurrentWindowLabel())
) {
  logger.info('[Deep Link] Module initialized, calling listenToEvent()...');
  void listenToEvent();
}
